import { useEffect, useState } from "react"
import { useStrategyStore } from "@/stores/strategy-store"
import { GoNoGoAssessment } from "@/components/strategy/GoNoGoAssessment"
import { GoNoGoStickyStrip } from "@/components/strategy/GoNoGoStickyStrip"

interface GoNoGoSectionProps {
  bidId: string
  /** The reader can't edit this bid (e.g. view-only access). */
  readOnly?: boolean
}

/**
 * Go/No-Go view (first sub-tab of Strategy) — the bid's decision gate. Owns
 * assessment hydration (StrategySection does not load it) and resumes any in-flight
 * AI scoring so leaving/returning to the tab doesn't lose progress.
 */
export function GoNoGoSection({ bidId, readOnly = false }: GoNoGoSectionProps) {
  const assessment = useStrategyStore((s) => s.assessment)
  const { loadAssessment, resumeAiScoringIfNeeded, reset } = useStrategyStore()
  // Held in state, not a ref, so the observer re-attaches when the assessment
  // view swaps branches (reopening a decision) and hands us a NEW sentinel
  // node. An observer left on the old, detached node reports "not
  // intersecting" forever, which pins the strip on permanently.
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null)
  const [pastHero, setPastHero] = useState(false)

  useEffect(() => {
    const hydrate = async () => {
      // loadAssessment first so resumeAiScoringIfNeeded can check
      // assessment.aiScoringStatus to decide whether to restart polling.
      await loadAssessment(bidId)
      void resumeAiScoringIfNeeded(bidId)
    }
    void hydrate()
    return () => reset()
  }, [bidId, loadAssessment, resumeAiScoringIfNeeded, reset])

  // The strip appears once the sentinel — which sits at the hero score card's
  // bottom edge — has scrolled up OUT of the viewport. "Above the top", not
  // merely "not visible": a sentinel still below the fold is equally
  // non-intersecting, and treating that as "past" showed the strip at rest,
  // parked over the top of the page content.
  useEffect(() => {
    if (!sentinel || typeof IntersectionObserver === "undefined") return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return
        const rootTop = entry.rootBounds?.top ?? 0
        setPastHero(entry.boundingClientRect.bottom <= rootTop)
      },
      { rootMargin: "0px" }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [sentinel])

  const showStrip = pastHero && assessment?.status === "in_progress"

  return (
    <div>
      {/* Zero-height sticky slot. It is always in the tree and takes no space,
          so mounting the strip cannot push the sentinel back into view — a
          shift there would flip the observer and flicker the strip every
          frame. The strip itself is absolutely positioned inside it. */}
      <div className="sticky top-0 z-10 h-0">
        {showStrip && assessment && <GoNoGoStickyStrip assessment={assessment} />}
      </div>
      <GoNoGoAssessment bidId={bidId} heroSentinelRef={setSentinel} readOnly={readOnly} />
    </div>
  )
}
